import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/* Set of paths recognized as viewport-modal-bearing player surfaces.
   Keep this in sync with the modal Routes block in App.jsx. */
const PLAYER_MODAL_PATHS = new Set([
  '/player',
  '/player/wholesaler',
  '/bank/drugs',
  '/bank/concepts',
]);

/**
 * usePlayerSurfaceNav — single source of truth for opening a player
 * config surface (Wholesaler / Stats / Drug Bank / Concept Vault) as a
 * viewport modal.
 *
 * Returns { open(path) } that:
 *   1. Captures the original backgroundLocation when chaining (so
 *      Pharmacy → Wholesaler keeps the dashboard underneath instead
 *      of stacking modal-on-modal).
 *   2. Always passes viewportOrb: true so the modal Routes block in
 *      App.jsx fires.
 *
 * Call sites should never inline this pattern manually — they will
 * forget the chain-preservation step.
 */
export function usePlayerSurfaceNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const open = useCallback((path) => {
    if (!PLAYER_MODAL_PATHS.has(path)) {
      // Not a player-modal-bearing route. Do a normal nav. We don't
      // throw because the caller may legitimately route to /player/journey
      // (an intentional full-page boundary) and we want a single entry.
      navigate(path);
      return;
    }
    const bg = location.state?.backgroundLocation || location;
    navigate(path, {
      state: { backgroundLocation: bg, viewportOrb: true },
    });
  }, [navigate, location]);
  return { open };
}
